import { useMemo, memo } from 'react'
import { cn } from '@shared/lib/utils'
import { PANEL_SPACING } from '../../../constants/spacing'
import { NavigationHeader } from './components/NavigationHeader'
import { useEntityStatus } from '../../../hooks/useEntityStatus'
import { CustomerTypeBadge } from '@features/customers/components/CustomerTypeBadge'

type FieldFormatter<T> = (value: T[keyof T]) => string

export interface EntityStatusPanelConfig<T> {
  entityType: string
  entityLabel: string
  primaryField: keyof T
  primaryFieldFormatter?: FieldFormatter<T>
  secondaryField?: keyof T
  secondaryFieldFormatter?: FieldFormatter<T>
}

export interface EntityStatusPanelProps<T extends { id?: string | number; status?: string; transactionRef?: string; type?: string }> {
  entityInfo: T
  config: EntityStatusPanelConfig<T>
  onBack?: () => void
  onOpenFullDetails?: () => void
  isDetailView?: boolean
  totalItems?: number
  navigatePrevious?: () => void
  navigateNext?: () => void
  canNavigatePrevious?: boolean
  canNavigateNext?: boolean
  metadataComponent: React.ComponentType<{ info: T }>
  actionsComponent?: React.ComponentType<{ entityInfo: T }>
  /**
   * Optional typography overrides. Defaults reproduce the standard panel look,
   * so existing entity types are unaffected. Payments/refunds/disputes pass
   * balance-hub matching values via PaymentEntityStatusPanel.
   */
  styleOverrides?: {
    /** Classes for the hero primary value (e.g. the amount). */
    primaryValueClassName?: string
    /** Classes for the small entity label above the primary value. */
    entityLabelClassName?: string
  }
}

const DEFAULT_ENTITY_LABEL_CLASS = 'text-xs text-muted-foreground tracking-wide font-medium font-mono uppercase'
const DEFAULT_PRIMARY_VALUE_CLASS = 'font-bold text-foreground text-5xl leading-tight'

const EntityStatusPanelComponent = <T extends { id?: string | number; status?: string; transactionRef?: string; type?: string }>({
  entityInfo,
  config,
  onBack,
  onOpenFullDetails,
  isDetailView = false,
  totalItems = 1,
  navigatePrevious,
  navigateNext,
  canNavigatePrevious,
  canNavigateNext,
  metadataComponent: MetadataComponent,
  actionsComponent: ActionsComponent,
  styleOverrides
}: EntityStatusPanelProps<T>) => {
  const statusInfo = useEntityStatus(config.entityType, entityInfo)
  const entityLabelClassName = styleOverrides?.entityLabelClassName ?? DEFAULT_ENTITY_LABEL_CLASS
  const primaryValueClassName = styleOverrides?.primaryValueClassName ?? DEFAULT_PRIMARY_VALUE_CLASS

  const primaryValue = useMemo(() => {
    const fieldValue = entityInfo[config.primaryField]
    return config.primaryFieldFormatter
      ? config.primaryFieldFormatter(fieldValue)
      : String(fieldValue ?? 'N/A')
  }, [entityInfo, config])

  const secondaryValue = useMemo(() => {
    if (!config.secondaryField) return null
    const fieldValue = entityInfo[config.secondaryField]
    return config.secondaryFieldFormatter
      ? config.secondaryFieldFormatter(fieldValue)
      : String(fieldValue ?? '')
  }, [entityInfo, config])

  return (
    <div className={cn(
      "rounded-3xl overflow-hidden",
      // In the full detail view the header is flat (no card, border or shadow)
      // to match the balance-hub style. Elsewhere (summary/preview) keep the card.
      isDetailView
        ? "bg-transparent"
        : "bg-white border shadow-[4px_8px_24px_0_rgba(11,59,69,0.08),0_1px_2px_0_rgba(11,59,69,0.06)]",
      PANEL_SPACING.CONTAINER_BOTTOM_MARGIN
    )}>
      {statusInfo?.styling && (
        <div className="p-2">
          <div className={cn(
            "h-10 rounded-xl flex items-center justify-start px-6 py-3",
            statusInfo.styling.bgColor
          )}>
            <p className={cn(
              "font-mono text-xs tracking-wide uppercase",
              statusInfo.styling.textColor
            )}>
              {statusInfo.styling.label}
            </p>
          </div>
        </div>
      )}

      {!isDetailView && (
        <div className="px-2">
          <NavigationHeader
            onBack={onBack}
            entityType={config.entityType}
            onOpenFullDetails={onOpenFullDetails}
            totalItems={totalItems}
            navigatePrevious={navigatePrevious}
            navigateNext={navigateNext}
            canGoPrevious={canNavigatePrevious}
            canGoNext={canNavigateNext}
          />
        </div>
      )}

      <div className={PANEL_SPACING.CARD_CONTENT_PADDING}>
        <div className="flex flex-col gap-2 mb-6">
          <p className={entityLabelClassName}>
            {config.entityLabel}
          </p>
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-3">
              <p className={primaryValueClassName}>
                {primaryValue}
              </p>
              {config.entityType === 'customer' && entityInfo.type && (entityInfo.type === 'individual' || entityInfo.type === 'business') && (
                <CustomerTypeBadge
                  type={entityInfo.type}
                  className="mt-1"
                />
              )}
            </div>
            <div className="flex gap-2">
              {ActionsComponent && <ActionsComponent entityInfo={entityInfo} />}
            </div>
          </div>
          {secondaryValue && (
            <p className="text-sm mt-1">
              <span className="text-muted-foreground">on </span>
              <span className="text-primary">{secondaryValue}</span>
            </p>
          )}
        </div>

        <MetadataComponent info={entityInfo} />
      </div>
    </div>
  )
}

// Export memoized component with display name for better debugging
EntityStatusPanelComponent.displayName = 'EntityStatusPanel'

export const EntityStatusPanel = memo(EntityStatusPanelComponent) as typeof EntityStatusPanelComponent
